import { useEffect, useState, type FC, type ReactNode } from 'react';
import { LayoutStack } from '../../components/layouts/LayoutStack';
import { DefaultImage } from '../shared';
import WWLogo from 'assets/images/logos/wirwunder_logo_rgb.svg';
import SparkassenLogo from './sparkasse.svg';
import getSafeImageSource from '../../utils/getSafeImageSource';
import MatchingEventBanner from './MatchingEventBanner';
import ModalContent, { type ModalContentText } from './ModalContent';
import type { MatchingEventBannerData } from './types';
import Modal from '../../components/Modal';
import styles from './MatchingEventBanners.module.css';
import classNames from 'classnames';
const wwLogo = getSafeImageSource(WWLogo);
const sparkassenLogo = getSafeImageSource(SparkassenLogo);
function BannerList({
  matchingEvents,
  onSelect,
  Image = DefaultImage,
  buttonText
}: {
  matchingEvents: Array<Pick<MatchingEventBannerData, 'id' | 'content' | 'color' | 'logoSrc' | 'logoAlt' | 'isWW' | 'branchLogoRed'>>;
  onSelect: (matchingEventId: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Image: FC<any>;
  buttonText: string;
}) {
  return matchingEvents.map(({
    isWW,
    content,
    color,
    logoSrc: logoSrc_,
    branchLogoRed: branchLogo,
    logoAlt: logoAlt_,
    id
  }) => {
    const logoSrc = isWW ? wwLogo : logoSrc_;
    const logoAlt = isWW ? 'wirwunder' : logoAlt_;
    const logoSecondarySrc = isWW && branchLogo ? sparkassenLogo : branchLogo;
    function onClick() {
      onSelect(id);
    }
    return <MatchingEventBanner key={id} color={color} buttonText={buttonText} logoSrc={logoSrc} logoAlt={logoAlt} logoSecondarySrc={logoSecondarySrc} logoSecondaryAlt={logoAlt_} onClick={onClick} Image={Image}>
          {content}
        </MatchingEventBanner>;
  });
}
export type MatchingEventBannersText = {
  buttonText: string;
  campaignTypes: {
    spendenmarathon: string;
    verdopplungsaktion: string;
  };
  modal: ModalContentText;
};
export type MatchingEventBannersProps = {
  text: MatchingEventBannersText;
  matchingEvents: Array<MatchingEventBannerData>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Image?: FC<any>;
  locale: string;
  children?: ReactNode;
  className?: string;
};
function getTitle(matchingEvent: MatchingEventBannerData | undefined | null, text: MatchingEventBannersText) {
  if (!matchingEvent) return '';
  const {
    isWW,
    campaignType,
    title
  } = matchingEvent;
  if (isWW) {
    if (campaignType === 'SPENDENMARATHON') return text.campaignTypes.spendenmarathon;
    if (campaignType === 'VERDOPPLUNGSAKTION') return text.campaignTypes.verdopplungsaktion;
  }
  return title ?? text.campaignTypes.verdopplungsaktion;
}
function MatchingEventBanners({
  text,
  matchingEvents,
  Image = DefaultImage,
  locale,
  children,
  className
}: MatchingEventBannersProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [openMatchingEvents, setOpenMatchingEvents] = useState<boolean>(false);
  const matchingEvent_0 = matchingEvents.find(matchingEvent => matchingEvent.id === selected);
  const title = getTitle(matchingEvent_0, text);
  function onSelect(matchingEventId: string) {
    setSelected(matchingEventId);
    setShowModal(true);
  }
  function onCloseComplete() {
    setSelected(null);
  }
  function onClose() {
    setShowModal(false);
  }
  const hasMatchingEvents = !!matchingEvents.length;
  useEffect(() => {
    const timeout = setTimeout(() => {
      setOpenMatchingEvents(hasMatchingEvents);
    }, 33);
    return () => clearTimeout(timeout);
  }, [hasMatchingEvents]);
  return <div className={classNames(styles.container, className, {
    [styles.open!]: openMatchingEvents
  })}>
      <LayoutStack>
        <BannerList matchingEvents={matchingEvents} onSelect={onSelect} Image={Image} buttonText={text.buttonText} />
        <Modal title={title} isOpen={!!showModal} onClose={onClose} onCloseComplete={onCloseComplete} closeText={text.modal.close} titleClassName={styles.modalTitle} hideActions>
          {matchingEvent_0 && <ModalContent text={text.modal} onHide={onClose} Image={Image} locale={locale} {...matchingEvent_0} actor={matchingEvent_0.actor ?? null} matchingPercentage={matchingEvent_0.matchingPercentage ?? null} hasOnlyOneProject={matchingEvent_0.hasOnlyOneProject ?? false} usesSleepyCoins={matchingEvent_0.usesSleepyCoins ?? false} branchLogo={matchingEvent_0.branchLogoRed ?? null} logo={matchingEvent_0.logoSrc ?? null} campaignType={matchingEvent_0.campaignType ?? null} maximumMatchingAmountInCents={matchingEvent_0.maximumMatchingAmountInCents ?? null} isPostCodeLottery={matchingEvent_0.isPostCodeLottery ?? false}>
              {children}
            </ModalContent>}
        </Modal>
      </LayoutStack>
    </div>;
}
export default MatchingEventBanners;